import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { rideAPI, notificationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Car, MapPin, Clock, Bell, User, LogOut, Plus, History, Home,
  Calendar, Loader2, Phone, CheckCircle, XCircle, AlertCircle, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Thirugoo Logo Component
const ThirugooLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="/thirugoo-logo.png" 
    alt="Thirugoo" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('book');
  const [rides, setRides] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPaymentQR, setShowPaymentQR] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  
  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    pickup_location: '',
    drop_location: '',
    scheduled_time: '',
    notes: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'customer') {
      navigate(user.role === 'admin' ? '/admin' : '/driver');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [ridesRes, notifRes, unreadRes] = await Promise.all([
        rideAPI.getByCustomer(user.id),
        notificationAPI.getByUser(user.id),
        notificationAPI.getUnreadCount(user.id)
      ]);
      setRides(ridesRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleBookRide = async (e) => {
    e.preventDefault();
    if (!bookingForm.pickup_location || !bookingForm.drop_location || !bookingForm.scheduled_time) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      const rideData = {
        ...bookingForm,
        scheduled_time: new Date(bookingForm.scheduled_time).toISOString()
      };
      await rideAPI.create(rideData, user.id);
      toast.success('Ride booked successfully! We will assign a driver soon.');
      setBookingForm({ pickup_location: '', drop_location: '', scheduled_time: '', notes: '' });
      loadData();
      setActiveTab('rides');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book ride');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRide = async (rideId) => {
    try {
      await rideAPI.cancel(rideId);
      toast.success('Ride cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await notificationAPI.markAllRead(user.id);
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking notifications read:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      started: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.toUpperCase()}</Badge>;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ThirugooLogo className="h-9 w-9" />
            <span className="text-xl font-bold text-gray-900">Thirugoo</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setActiveTab('notifications')}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Welcome, {user.name}!</h1>
          <p className="text-gray-600">Book your ride with Thirugoo</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="book" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Book</span>
            </TabsTrigger>
            <TabsTrigger value="rides" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">My Rides</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2 relative">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Alerts</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
          </TabsList>

          {/* Book Ride Tab */}
          <TabsContent value="book">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-5 w-5 text-amber-600" />
                  Book a Ride
                </CardTitle>
                <CardDescription>Fill in the details to book your ride</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBookRide} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickup">Pickup Location *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                      <Input
                        id="pickup"
                        placeholder="Enter pickup address"
                        value={bookingForm.pickup_location}
                        onChange={(e) => setBookingForm({ ...bookingForm, pickup_location: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="drop">Drop Location *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                      <Input
                        id="drop"
                        placeholder="Enter drop address"
                        value={bookingForm.drop_location}
                        onChange={(e) => setBookingForm({ ...bookingForm, drop_location: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="datetime">Pickup Date & Time *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="datetime"
                        type="datetime-local"
                        value={bookingForm.scheduled_time}
                        onChange={(e) => setBookingForm({ ...bookingForm, scheduled_time: e.target.value })}
                        className="pl-10"
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any special requirements? (e.g., airport pickup, luggage help)"
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Booking...</>
                    ) : (
                      <><Car className="mr-2 h-5 w-5" /> Book Ride</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Rides Tab */}
          <TabsContent value="rides">
            <div className="space-y-4">
              {rides.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No rides yet. Book your first ride!</p>
                    <Button 
                      onClick={() => setActiveTab('book')} 
                      className="mt-4 bg-amber-600 hover:bg-amber-700"
                    >
                      Book a Ride
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                rides.map((ride) => (
                  <Card key={ride.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-sm text-gray-500">
                            {format(new Date(ride.scheduled_time), 'PPp')}
                          </p>
                          {getStatusBadge(ride.status)}
                        </div>
                        <p className="text-lg font-bold text-amber-600">
                          Rs. {ride.fare_estimate?.toFixed(0) || '---'}
                        </p>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-sm">{ride.pickup_location}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                          <span className="text-sm">{ride.drop_location}</span>
                        </div>
                      </div>

                      {ride.driver_name && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          <p className="text-sm font-medium">Driver: {ride.driver_name}</p>
                          {ride.vehicle_info && (
                            <p className="text-sm text-gray-600">{ride.vehicle_info}</p>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        {ride.status === 'pending' && (
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleCancelRide(ride.id)}
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                        )}
                        {ride.status === 'completed' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedRide(ride); setShowPaymentQR(true); }}
                          >
                            <QrCode className="h-4 w-4 mr-1" /> Pay Now
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-amber-600" />
                  Notifications
                </CardTitle>
                {unreadCount > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllNotificationsRead}>
                    Mark all read
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="py-8 text-center">
                    <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No notifications yet</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-3 rounded-lg border ${!notif.is_read ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}
                        >
                          <div className="flex items-start gap-3">
                            {notif.notification_type === 'success' ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                            ) : notif.notification_type === 'warning' ? (
                              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                            ) : (
                              <Bell className="h-5 w-5 text-blue-500 mt-0.5" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{notif.title}</p>
                              <p className="text-sm text-gray-600">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {format(new Date(notif.created_at), 'PPp')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-amber-600" />
                  My Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{user.name}</p>
                    <p className="text-gray-600 flex items-center gap-1">
                      <Phone className="h-4 w-4" /> +91 {user.phone}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-amber-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-amber-600">{rides.length}</p>
                    <p className="text-sm text-gray-600">Total Rides</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {rides.filter(r => r.status === 'completed').length}
                    </p>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                </div>

                <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                  <Home className="mr-2 h-4 w-4" /> Back to Home
                </Button>
                <Button variant="destructive" className="w-full" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Payment QR Dialog */}
      <Dialog open={showPaymentQR} onOpenChange={setShowPaymentQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pay for Your Ride</DialogTitle>
            <DialogDescription>
              Scan the QR code to pay Rs. {selectedRide?.fare_estimate?.toFixed(0) || '---'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              {/* Placeholder QR - In production, use actual UPI QR */}
              <div className="w-48 h-48 bg-white border-2 border-dashed border-gray-300 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <QrCode className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Owner's UPI QR</p>
                  <p className="text-xs text-gray-400">thirumalesh@upi</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 text-center">
              Pay via UPI to: <strong>thirumalesh@upi</strong>
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Contact: 9164212758 for any payment issues
            </p>
          </div>
          <Button onClick={() => setShowPaymentQR(false)} className="w-full">
            Done
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
